import { Component, Input, OnInit, Output, EventEmitter, OnChanges, ElementRef, ViewChild, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { HoleLoad, HoleStatus, HoleStatusHistoryUtil, Blast } from '../../services/hole.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ImportModalComponent } from '../import-modal/import-modal.component';

@Component({
  selector: 'app-holes-load-list',
  templateUrl: './holes-load-list.component.html',
  styleUrls: ['./holes-load-list.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule]
})
export class BlastLoadingListComponent implements OnInit, OnChanges {
  @Input() holes: HoleLoad[] = [];
  @Input() blast: Blast;
  @Input() tirId: number;
  @Input() qualityData: any;
  @Input() selectedHoleId: string | null = null;
  @Output() addHole = new EventEmitter<void>();
  @Output() holeSelected = new EventEmitter<string | null>();
  
  @ViewChild('holesContainer', { static: false }) holesContainer!: ElementRef;
  
  expandedHoleId: string | null = null;
  expandedSections: Map<string, Set<string>> = new Map();
  searchTerm = '';
  filteredHoles: HoleLoad[] = [];
  importModal: BsModalRef;
  constructor(private readonly modalService: BsModalService) {

  }
  ngOnInit() {
    this.updateFilteredHoles();
    this.expandedHoleId = this.selectedHoleId;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updateFilteredHoles();
    if (changes['selectedHoleId'] && changes['selectedHoleId'].currentValue && 
        changes['selectedHoleId'].currentValue !== changes['selectedHoleId'].previousValue) {
      if(this.searchTerm !== ''){
        this.searchTerm = '';
        this.updateFilteredHoles();
      }
      this.selectAndScrollToHole(changes['selectedHoleId'].currentValue);
    } else if (changes['selectedHoleId'] && !changes['selectedHoleId'].currentValue) {
      this.expandedHoleId = null;
    }
  }

  updateFilteredHoles() {
    if (!this.searchTerm.trim()) {
      this.filteredHoles = [...this.holes];
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.filteredHoles = this.holes.filter(hole => 
        ('Hole ' + hole.name).toLowerCase().includes(searchLower)
      );
    }
  }

  onSearchChange() {
    this.updateFilteredHoles();
    this.expandedHoleId = null;
    this.holeSelected.emit(null);
  }

    onAddHole() {
      var initialState = {
        uploadUrl: 'Blasts/' + this.tirId + '/blastFromStr',
        allowStrFileImport: true
      };
      this.importModal = this.modalService.show(ImportModalComponent, { backdrop: true, ignoreBackdropClick: true, keyboard: false, initialState, class: 'modal-dialog-centered' });
      const subscription = this.modalService.onHide.subscribe({
        next: () => {
          this.addHole.emit();
          subscription.unsubscribe();
        },
        error: (error) => {
          console.error('Error with modal subscription:', error);
        },
        complete: () => {
          subscription.unsubscribe();
        }
      });
    }

  clearSearch() {
    this.searchTerm = '';
    this.updateFilteredHoles();
  }

  toggleHole(holeId: string) {
    if (this.expandedHoleId === holeId) {
      this.expandedHoleId = null;
      this.expandedSections.delete(holeId);
      this.holeSelected.emit(null);
    } else {
      this.expandedHoleId = holeId;
      this.holeSelected.emit(holeId);
    }
  }

  isHoleExpanded(holeId: string): boolean {
    return this.expandedHoleId === holeId;
  }

  toggleSection(holeId: string, sectionName: string) {
    if (!this.expandedSections.has(holeId)) {
      this.expandedSections.set(holeId, new Set());
    }
    
    const holeSections = this.expandedSections.get(holeId)!;
    if (holeSections.has(sectionName)) {
      holeSections.delete(sectionName);
    } else {
      holeSections.add(sectionName);
    }
  }

  isSectionExpanded(holeId: string, sectionName: string): boolean {
    const holeSections = this.expandedSections.get(holeId);
    return holeSections ? holeSections.has(sectionName) : false;
  }

  trackByHoleId(index: number, hole: HoleLoad): string {
    return hole.holeId;
  }

  private selectAndScrollToHole(holeId: string): void {
    this.expandedHoleId = holeId;
    setTimeout(() => {
      const holeElement = document.querySelector(`[data-hole-id="${holeId}"]`) as HTMLElement;
      if (holeElement && this.holesContainer) {
        const container = this.holesContainer.nativeElement;
        
        try {
          const containerRect = container.getBoundingClientRect();
          const elementRect = holeElement.getBoundingClientRect();
          const scrollTop = container.scrollTop + elementRect.top - containerRect.top - (containerRect.height / 2) + (elementRect.height / 2);
          container.scrollTo({
            top: Math.max(0, scrollTop - 100),
            behavior: 'smooth'
          });
        } catch (error) {
          console.warn('Scroll manuel échoué, utilisation du fallback:', error);
          holeElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }
    }, 150);
  }

  getHoleStatusHistory(hole: HoleLoad): HoleStatus[] {
      if (hole.statusHistory !== undefined) {
        return HoleStatusHistoryUtil.getStatusHistory(hole.statusHistory);
      }
      return [];
    }

  getStatusSvgPath(status: HoleStatus): string {
      switch (status) {
        case HoleStatus.MISSING:
          return 'assets/icon/missing.svg';
        case HoleStatus.COLLAPSED:
          return 'assets/icon/hole-collapsed.svg';
        case HoleStatus.DRILLED:
          return 'assets/icon/hole-drilled.svg';
        case HoleStatus.TOADJUST:
          return 'assets/icon/hole-to-adjust.svg';
        case HoleStatus.REDRILLED:
          return 'assets/icon/hole-redrilled.svg';
        case HoleStatus.TO_REDRILL:
          return 'assets/icon/hole-to-redrill.svg';
        case HoleStatus.LOADED:
          return 'assets/icon/hole-loaded.svg';
        case HoleStatus.LOADING:
          return 'assets/icon/hole-loading.svg';
        case HoleStatus.BLASTED:
          return 'assets/icon/hole-blasted.svg';
        case HoleStatus.PLANNED:
          return 'assets/icon/hole-planned.svg';
        default:
          return 'assets/icon/default.svg';
      }
    }

  shouldShowQualityIcon(): boolean {
    return !!this.blast;
  }

  getBulkQualityClass(hole: HoleLoad): string {
    const qualityType = this.getQualityType(
      hole.plannedTotalBulk > 0 && hole.measuredTotalBulk > 0 ? Math.abs(hole.plannedTotalBulk - hole.measuredTotalBulk) : null,
      this.qualityData?.bulkGood, 
      this.qualityData?.bulkBad
    );
    return `quality-circle-${qualityType}`;
  }

  getSteamingQualityClass(hole: HoleLoad): string {
    const qualityType = this.getQualityType(
      hole.plannedSteaming > 0 && hole.measuredSteaming > 0 ? Math.abs(hole.plannedSteaming - hole.measuredSteaming) : null,
      this.qualityData?.steamingFinalGood, 
      this.qualityData?.steamingFinalBad
    );
    return `quality-circle-${qualityType}`;
  }

  getLinearQtQualityClass(hole: HoleLoad): string {
    const qualityType = this.getQualityType(
      hole.plannedLinearQt > 0 && hole.measuredLinearQt > 0 ? Math.abs(hole.plannedLinearQt - hole.measuredLinearQt) : null,
      this.qualityData?.linearQuantityGood, 
      this.qualityData?.linearQuantityBad
    );
    return `quality-circle-${qualityType}`;
  }

  private getQualityType(value: number, goodThreshold: number, badThreshold: number): string {
    if(value != null){
      if (value < goodThreshold) {
        return 'green';
      } else if (value >= goodThreshold && value <= badThreshold) {
        return 'yellow';
      } else {
        return 'red';
      }
    }
    
  }
}